'use client';

import type { CSSProperties, HTMLAttributes } from 'react';
import styles from './Icon.module.css';
import { useIconRenderer, type IconSize } from './icon-family';

export type { IconSize };

interface IconProps {
  /** Icon name — passed through to the active icon family's renderer verbatim. */
  name: string;
  /** Icon size preset, or a raw pixel number as an escape (D7) — opsz tracks px exactly. */
  size?: IconSize | number;
  /** Toggle filled variant (FILL 1) vs outline (FILL 0) */
  filled?: boolean;
  /** CSS color value */
  color?: string;
  /** Font weight (100-700), default 300 */
  weight?: number;
  className?: HTMLAttributes<HTMLSpanElement>['className'];
  style?: CSSProperties;
}

// One size source (Q3, R1 B8): the five preset numbers live HERE and nowhere else. The CSS box
// (width/height) is set from this same number via the inline style below — Icon.module.css's
// five duplicate size classes are gone (D7).
const SIZE_PX: Record<IconSize, number> = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
  xl: 48,
};

function Icon({
  name,
  size = 'md',
  filled = false,
  color,
  weight = 300,
  className = '',
  style,
}: IconProps) {
  const render = useIconRenderer();
  const px = typeof size === 'number' ? size : SIZE_PX[size];

  return render(name, {
    size,
    px,
    filled,
    weight,
    className: `${styles.icon} ${className}`.trim(),
    style: {
      width: px,
      height: px,
      color: color || undefined,
      ...style,
    },
  });
}

Icon.displayName = 'Icon';
export default Icon;
